import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Switch,
  Row,
  Col,
  Divider
} from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  ShopOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import {
  createFirmNameRequest,
  updateFirmNameRequest
} from '../../../redux/actions/firmNameActions';
import '../../../styles/forms.css';

const { TextArea } = Input;

const FirmNameForm = ({ onCancel }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { editingFirmName, creating, updating } = useSelector(state => state.firmNames);

  const isEditing = !!editingFirmName;
  const isSubmitting = creating || updating;

  useEffect(() => {
    if (editingFirmName) {
      form.setFieldsValue({
        firm_name: editingFirmName.firm_name,
        address: editingFirmName.address,
        gst_number: editingFirmName.gst_number,
        mobile_number: editingFirmName.mobile_number,
        is_active: editingFirmName.is_active !== undefined ? editingFirmName.is_active : true
      });
    } else {
      form.setFieldsValue({
        is_active: true
      });
    }
  }, [editingFirmName, form]);

  const handleSubmit = (values) => {
    const firmData = {
      firm_name: values.firm_name.trim(),
      address: values.address.trim(),
      gst_number: values.gst_number.trim().toUpperCase(),
      mobile_number: values.mobile_number.trim(),
      is_active: values.is_active !== undefined ? values.is_active : true
    };

    if (isEditing) {
      dispatch(updateFirmNameRequest(editingFirmName.id, firmData));
    } else {
      dispatch(createFirmNameRequest(firmData));
    }

    // Close modal after successful submission (handled in saga)
    onCancel();
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Validation rules
  const validateFirmName = (_, value) => {
    if (!value || !value.trim()) {
      return Promise.reject(new Error('Firm name is required'));
    }
    if (value.trim().length < 2) {
      return Promise.reject(new Error('Firm name must be at least 2 characters'));
    }
    if (value.trim().length > 255) {
      return Promise.reject(new Error('Firm name must not exceed 255 characters'));
    }
    return Promise.resolve();
  };

  const validateAddress = (_, value) => {
    if (!value || !value.trim()) {
      return Promise.reject(new Error('Address is required'));
    }
    if (value.trim().length < 10) {
      return Promise.reject(new Error('Address must be at least 10 characters'));
    }
    return Promise.resolve();
  };

  const validateGSTNumber = (_, value) => {
    if (!value || !value.trim()) {
      return Promise.reject(new Error('GST number is required'));
    }
    const gstPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gstPattern.test(value.trim().toUpperCase())) {
      return Promise.reject(new Error('Invalid GST number format (e.g., 22AAAAA0000A1Z5)'));
    }
    return Promise.resolve();
  };

  const validateMobileNumber = (_, value) => {
    if (!value || !value.trim()) {
      return Promise.reject(new Error('Mobile number is required'));
    }
    const mobilePattern = /^[6-9][0-9]{9}$/;
    if (!mobilePattern.test(value.trim())) {
      return Promise.reject(new Error('Invalid mobile number (must be 10 digits starting with 6-9)'));
    }
    return Promise.resolve();
  };

  return (
    <div className="professional-form">
      {/* Form Header */}
      <div className="form-modal-header">
        <div className="form-modal-icon">
          <ShopOutlined />
        </div>
        <div className="form-modal-title">
          <h3>{isEditing ? 'Edit Firm Details' : 'New Firm Registration'}</h3>
          <p>Register your firm with complete business details</p>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark="optional"
      >
        {/* Basic Information */}
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              label="Firm Name"
              name="firm_name"
              rules={[{ validator: validateFirmName }]}
              tooltip="Enter your registered business name"
            >
              <Input
                prefix={<ShopOutlined style={{ color: '#999' }} />}
                placeholder="e.g., ABC Enterprises, XYZ Printers Pvt Ltd"
                maxLength={255}
                showCount
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              label="Complete Address"
              name="address"
              rules={[{ validator: validateAddress }]}
              tooltip="Include street, city, state, and postal code"
            >
              <TextArea
                prefix={<EnvironmentOutlined />}
                placeholder="Enter complete address with city, state, and pincode"
                rows={3}
                maxLength={500}
                showCount
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" style={{ margin: '1.5rem 0 1rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#666' }}>Tax & Contact Details</span>
        </Divider>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="GST Number"
              name="gst_number"
              rules={[{ validator: validateGSTNumber }]}
              tooltip="15-digit GST identification number"
            >
              <Input
                placeholder="22AAAAA0000A1Z5"
                maxLength={15}
                style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Mobile Number"
              name="mobile_number"
              rules={[{ validator: validateMobileNumber }]}
              tooltip="10-digit contact number"
            >
              <Input
                prefix={<PhoneOutlined style={{ color: '#999' }} />}
                placeholder="9876543210"
                maxLength={10}
                style={{ fontFamily: 'monospace' }}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: '1rem 0' }} />

        <Row gutter={16}>
          <Col xs={24}>
            <div className="status-badge-group">
              <div className="status-badge-item">
                <span className="status-badge-label">Firm Status</span>
                <Form.Item
                  name="is_active"
                  valuePropName="checked"
                  style={{ margin: 0 }}
                >
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                  />
                </Form.Item>
              </div>
              <div className="input-help-text">
                <InfoCircleOutlined />
                Inactive firms won't be available for selection
              </div>
            </div>
          </Col>
        </Row>

        <div className="form-actions">
          <Button onClick={handleCancel} size="large">
            <CloseOutlined />
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            icon={<SaveOutlined />}
            size="large"
          >
            {isEditing ? 'Update Firm' : 'Register Firm'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default FirmNameForm;
